<template>
  <div class="email-body-iframe-container" :class="framed ? 'rounded-lg overflow-hidden border border-secondary-200 dark:border-secondary-700 shadow-md dark:shadow-lg' : ''">
    <iframe
      ref="iframeRef"
      :srcdoc="sanitizedHtml"
      sandbox="allow-same-origin allow-popups"
      @load="handleLoad"
      class="email-iframe"
      :style="{ height: iframeHeight }"
    ></iframe>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue';
import { sanitizeHtml, resolveCidImages } from '@/utils';
import emailService from '@/services/emailService';

const props = withDefaults(defineProps<{
  htmlContent: string;
  attachments?: any[];
  emailId?: string;
  framed?: boolean;
}>(), {
  framed: true,
});

const iframeRef = ref<HTMLIFrameElement | null>(null);
const iframeHeight = ref('auto');

const sanitizedHtml = ref('');

const enrichedIds = new Set<string>();

watch([() => props.htmlContent, () => props.attachments], ([newContent, atts]) => {
  // Siempre se pasa por resolveCidImages: sin adjuntos que las respalden, las
  // referencias cid: se descartan en vez de quedar como imagenes rotas.
  sanitizedHtml.value = sanitizeHtml(resolveCidImages(newContent, atts || []));

  if (props.emailId && !enrichedIds.has(props.emailId)) {
    const hasCidRefs = /src=["']cid:/i.test(newContent);
    const hasInlineWithoutContentId = atts?.some(
      (a: any) => a.isInline && !a.contentId
    );
    if (hasCidRefs && hasInlineWithoutContentId) {
      enrichedIds.add(props.emailId);
      emailService.enrichAttachmentContentIds(props.emailId).catch(() => {});
    }
  }
}, { immediate: true });

function handleLoad() {
  if (!iframeRef.value) return;
  
  try {
    const iframeDoc = iframeRef.value.contentDocument || iframeRef.value.contentWindow?.document;
    if (!iframeDoc) return;

    // Fondo blanco siempre para mejor visualización
    const backgroundColor = '#ffffff';
    
    // Agregar estilos base
    const style = iframeDoc.createElement('style');
    style.textContent = `
      body {
        margin: 0;
        padding: 16px;
        overflow-x: hidden;
        word-wrap: break-word;
        background-color: ${backgroundColor};
      }
      img {
        max-width: 100% !important;
        height: auto !important;
      }
      table {
        max-width: 100% !important;
      }
      * {
        box-sizing: border-box;
      }
    `;
    iframeDoc.head.appendChild(style);

    // Ajustar altura inicial inmediatamente
    updateHeight();

    // Usar ResizeObserver para detectar cambios automáticamente
    const resizeObserver = new ResizeObserver(() => {
      updateHeight();
    });
    resizeObserver.observe(iframeDoc.body);

    // Re-ajustar altura después de cargar imágenes
    const images = iframeDoc.querySelectorAll('img');
    
    if (images.length > 0) {
      let loadedCount = 0;
      const checkAllLoaded = () => {
        loadedCount++;
        if (loadedCount === images.length) {
          // Ajustar altura múltiples veces para asegurar corrección
          updateHeight();
          setTimeout(() => updateHeight(), 100);
          setTimeout(() => updateHeight(), 300);
          setTimeout(() => updateHeight(), 500);
        }
      };

      images.forEach((img: any) => {
        if (img.complete) {
          checkAllLoaded();
        } else {
          img.addEventListener('load', checkAllLoaded);
          img.addEventListener('error', checkAllLoaded);
        }
      });
    } else {
      // Si no hay imágenes, ajustar altura múltiples veces
      setTimeout(() => updateHeight(), 50);
      setTimeout(() => updateHeight(), 150);
    }
  } catch (error) {
    console.error('Error adjusting iframe height:', error);
  }
}



function updateHeight() {
  if (!iframeRef.value) return;
  
  try {
    const iframeDoc = iframeRef.value.contentDocument || iframeRef.value.contentWindow?.document;
    if (!iframeDoc) return;
    
    const height = iframeDoc.documentElement.scrollHeight;
    iframeHeight.value = `${height}px`;
  } catch (error) {
    console.error('Error updating iframe height:', error);
  }
}

onMounted(() => {
  // Observar cambios en el tamaño del contenido
  if (iframeRef.value) {
    const observer = new ResizeObserver(() => {
      updateHeight();
    });
    
    if (iframeRef.value.contentDocument?.body) {
      observer.observe(iframeRef.value.contentDocument.body);
    }
  }
});
</script>

<style scoped>
.email-body-iframe-container {
  width: 100%;
  overflow: hidden;
}

.email-iframe {
  width: 100%;
  border: none;
  display: block;
  overflow: hidden;
}
</style>
